import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import type { ScheduleAggregateFilters } from '../../../../types/workflows';
import { isEngineeringEnvironment } from '../../../../AppSettings/environmentConfig';

export interface ExportOptions {
  format: 'png' | 'pdf';
  includeLegend: boolean;
  includeFilters: boolean;
}

/**
 * Generate filename for schedule export
 * Format: schedule-{tenant}-{timestamp}.{ext}
 */
const generateFilename = (format: 'png' | 'pdf'): string => {
  const tenant = isEngineeringEnvironment ? 'engineering' : 'legal';
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-').split('T')[0];
  const ext = format === 'png' ? 'png' : 'pdf';
  return `schedule-${tenant}-${timestamp}.${ext}`;
};

/**
 * Format filters for display in export
 */
const formatFiltersForExport = (filters?: ScheduleAggregateFilters): string => {
  if (!filters) return '';
  
  const parts: string[] = [];
  
  if (filters.statuses && filters.statuses.length > 0) {
    parts.push(`Status: ${filters.statuses.join(', ')}`);
  }
  
  if (filters.disciplines && filters.disciplines.length > 0) {
    parts.push(`Disciplines: ${filters.disciplines.join(', ')}`);
  }
  
  if (filters.clientIds && filters.clientIds.length > 0) {
    parts.push(`Clients: ${filters.clientIds.length} selected`);
  }
  
  if (filters.startDate && filters.endDate) {
    parts.push(`Date Range: ${filters.startDate} to ${filters.endDate}`);
  }
  
  return parts.join(' | ');
};

/**
 * Export schedule as PNG
 */
export const exportAsPNG = async (
  chartElement: HTMLElement,
  legendElement: HTMLElement | null,
  filters?: ScheduleAggregateFilters,
  options: ExportOptions = { format: 'png', includeLegend: true, includeFilters: true }
): Promise<void> => {
  try {
    // Find the actual AmCharts canvas
    const am5Canvas = chartElement.querySelector('canvas');
    if (!am5Canvas) {
      throw new Error('Chart canvas not found. Please ensure the timeline is fully loaded.');
    }

    // Create a canvas to compose the export
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    if (!ctx) {
      throw new Error('Failed to get canvas context');
    }

    // Get dimensions
    const chartRect = am5Canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    let totalHeight = chartRect.height;
    let totalWidth = chartRect.width;
    
    // Add legend height if included
    if (options.includeLegend && legendElement) {
      const legendRect = legendElement.getBoundingClientRect();
      totalHeight += legendRect.height + 20; // Add padding
      totalWidth = Math.max(totalWidth, legendRect.width);
    }
    
    // Add filters height if included
    if (options.includeFilters && filters) {
      totalHeight += 60; // Space for filters text
    }

    canvas.width = totalWidth * dpr;
    canvas.height = totalHeight * dpr;
    ctx.scale(dpr, dpr);

    // Fill white background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, totalWidth, totalHeight);

    let currentY = 0;

    // Add filters text if included
    if (options.includeFilters && filters) {
      ctx.fillStyle = '#333333';
      ctx.font = '14px Arial';
      const filterText = formatFiltersForExport(filters);
      if (filterText) {
        const lines = filterText.match(/.{1,80}/g) || [filterText];
        lines.forEach((line, index) => {
          ctx.fillText(`Filters: ${line}`, 20, currentY + 20 + (index * 18));
        });
        currentY += lines.length * 18 + 20;
      }
    }

    // Draw legend if included
    if (options.includeLegend && legendElement) {
      const legendRect = legendElement.getBoundingClientRect();
      const legendCanvas = await htmlElementToCanvas(legendElement);
      if (legendCanvas) {
        ctx.drawImage(legendCanvas, 0, currentY, legendRect.width, legendRect.height);
        currentY += legendRect.height + 20;
      }
    }

    // Draw chart canvas directly
    ctx.drawImage(am5Canvas as HTMLCanvasElement, 0, currentY, chartRect.width, chartRect.height);

    // Convert to blob and download
    canvas.toBlob((blob) => {
      if (blob) {
        const filename = generateFilename('png');
        saveAs(blob, filename);
      } else {
        throw new Error('Failed to create PNG blob');
      }
    }, 'image/png', 0.95); // 0.95 quality
  } catch (error) {
    console.error('PNG export error:', error);
    throw error;
  }
};

/**
 * Export schedule as PDF
 */
export const exportAsPDF = async (
  chartElement: HTMLElement,
  legendElement: HTMLElement | null,
  filters?: ScheduleAggregateFilters,
  options: ExportOptions = { format: 'pdf', includeLegend: true, includeFilters: true }
): Promise<void> => {
  try {
    // Find the actual AmCharts canvas
    const am5Canvas = chartElement.querySelector('canvas');
    if (!am5Canvas) {
      throw new Error('Chart canvas not found. Please ensure the timeline is fully loaded.');
    }

    const pdf = new jsPDF({
      orientation: 'landscape',
      unit: 'px',
    });

    // Get dimensions
    const chartRect = am5Canvas.getBoundingClientRect();
    let currentY = 20;
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 20;

    // Add filters text if included
    if (options.includeFilters && filters) {
      pdf.setFontSize(10);
      pdf.setTextColor(51, 51, 51);
      const filterText = formatFiltersForExport(filters);
      if (filterText) {
        const lines = pdf.splitTextToSize(`Filters: ${filterText}`, pageWidth - 2 * margin);
        pdf.text(lines, margin, currentY);
        currentY += lines.length * 12 + 10;
      }
    }

    // Add legend if included
    if (options.includeLegend && legendElement) {
      const legendCanvas = await htmlElementToCanvas(legendElement);
      if (legendCanvas) {
        // Cap the legend at half the remaining page so the chart keeps the rest — and at
        // the page width: the rasterized legend is wider than a landscape page, so deriving
        // width from the height cap alone pushed legendX negative and clipped the left edge.
        const legendAspect = legendCanvas.width / legendCanvas.height;
        let legendHeight = Math.min(legendCanvas.height, (pageHeight - currentY - 40) / 2);
        let legendWidth = legendAspect * legendHeight;
        if (legendWidth > pageWidth - 2 * margin) {
          legendWidth = pageWidth - 2 * margin;
          legendHeight = legendWidth / legendAspect;
        }
        const legendX = (pageWidth - legendWidth) / 2;
        
        pdf.addImage(
          legendCanvas.toDataURL('image/png'),
          'PNG',
          legendX,
          currentY,
          legendWidth,
          legendHeight
        );
        currentY += legendHeight + 20;
      }
    }

    // Add chart directly from canvas
    const chartDataUrl = (am5Canvas as HTMLCanvasElement).toDataURL('image/png');
    
    // Calculate dimensions to fit on page
    const availableHeight = pageHeight - currentY - margin;
    const availableWidth = pageWidth - 2 * margin;
    
    // Maintain aspect ratio
    const chartAspectRatio = chartRect.width / chartRect.height;
    let chartWidth = availableWidth;
    let chartHeight = chartWidth / chartAspectRatio;
    
    if (chartHeight > availableHeight) {
      chartHeight = availableHeight;
      chartWidth = chartHeight * chartAspectRatio;
    }
    
    const chartX = (pageWidth - chartWidth) / 2;

    pdf.addImage(
      chartDataUrl,
      'PNG',
      chartX,
      currentY,
      chartWidth,
      chartHeight
    );

    // Save PDF
    const filename = generateFilename('pdf');
    pdf.save(filename);
  } catch (error) {
    console.error('PDF export error:', error);
    throw error;
  }
};

/**
 * Rasterize a plain HTML element (e.g. the legend) to a canvas via html2canvas.
 * html2canvas is imported lazily so the schedule page's initial load doesn't
 * carry the rasterizer — it only downloads on the first export.
 */
const rasterizeHtmlElement = async (element: HTMLElement): Promise<HTMLCanvasElement | null> => {
  try {
    const { default: html2canvas } = await import('html2canvas');

    // Capture the full scroll size so overflowing content isn't clipped
    const width = Math.ceil(Math.max(element.scrollWidth, element.offsetWidth));
    const height = Math.ceil(Math.max(element.scrollHeight, element.offsetHeight));

    return await html2canvas(element, {
      backgroundColor: '#ffffff',
      scale: window.devicePixelRatio || 1,
      useCORS: true,
      logging: false,
      width,
      height,
      windowWidth: width,
      windowHeight: height,
      scrollX: 0,
      scrollY: 0,
    });
  } catch (error) {
    console.warn('Failed to convert element to canvas:', error);
    return null;
  }
};

/**
 * Convert HTML element to canvas
 * For AmCharts, extracts the canvas directly
 * For other elements, rasterizes the DOM via html2canvas
 */
const htmlElementToCanvas = async (element: HTMLElement): Promise<HTMLCanvasElement | null> => {
  return new Promise((resolve) => {
    try {
      // For AmCharts timeline, look for the canvas element
      const canvas = element.querySelector('canvas');

      if (canvas) {
        // Clone the canvas
        const newCanvas = document.createElement('canvas');
        newCanvas.width = canvas.width;
        newCanvas.height = canvas.height;
        const ctx = newCanvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(canvas as HTMLCanvasElement, 0, 0);
          resolve(newCanvas);
          return;
        }
      }

      // For legend or other HTML elements, rasterize the rendered DOM.
      // rasterizeHtmlElement never rejects (it warns and yields null).
      resolve(rasterizeHtmlElement(element));
    } catch (error) {
      console.warn('Failed to convert element to canvas:', error);
      resolve(null);
    }
  });
};

